import { useMemo, CSSProperties } from 'react';

import { tokens } from '../theme/generated/tokens';
import { CssVarDefinitions } from '../theme/types';
import { cssVars } from '../utils';

export type InsetVars = {
  pt?: string;
  pr?: string;
  pb?: string;
  pl?: string;
};

export type DividerVars = {
  color?: string;
};

export type ActionsGroupVars = {
  gap?: string;
};

export type CardVars = {
  backgroundColor?: string;
  borderRadius?: string;
};

export type LayoutVars = {
  sectionSpacing?: string;
};

export const cardVars = cssVars<CardVars>(
  {
    backgroundColor: tokens.theme.bg.structural.surface,
    borderRadius: tokens.theme.radius.sm,
  },
  'card'
);

export const insetVars = cssVars<InsetVars>(
  {
    pt: '8px',
    pr: '16px',
    pb: '8px',
    pl: '16px',
  },
  'inset'
);

export const dividerVars = cssVars<DividerVars>(
  {
    color: tokens.theme.border.structural.neutral,
  },
  'divider'
);

export const actionsGroupVars = cssVars<ActionsGroupVars>(
  {
    gap: '12px',
  },
  'actionsGroup'
);

export const layoutVars = cssVars<LayoutVars>(
  {
    sectionSpacing: '24px',
  },
  'layout'
);

export type DsVars = {
  inset?: InsetVars;
  divider?: DividerVars;
  actionsGroup?: ActionsGroupVars;
  card?: CardVars;
  layout?: LayoutVars;
};
/**
 * Utility to transform `DsVars` object into the style object, containing the CSS variable definitions
 * @param vars - partial object, containing CSS variable values of every DS component
 * @returns CSS variable definitions, that can be passed right to HTML `style`
 */
export const toDsVarsDefinition = (vars: DsVars): CssVarDefinitions => {
  let result: Record<string, string> = {};
  if (vars.card) {
    result = { ...result, ...cardVars.setValues(vars.card) };
  }
  if (vars.inset) {
    result = { ...result, ...insetVars.setValues(vars.inset) };
  }
  if (vars.divider) {
    result = { ...result, ...dividerVars.setValues(vars.divider) };
  }
  if (vars.actionsGroup) {
    result = { ...result, ...actionsGroupVars.setValues(vars.actionsGroup) };
  }
  if (vars.layout) {
    result = { ...result, ...layoutVars.setValues(vars.layout) };
  }
  return result;
};

export const useDsVarsDefinitions = (vars?: DsVars): CssVarDefinitions => {
  return useMemo(() => (vars ? toDsVarsDefinition(vars) : {}), [vars]);
};

export const mergeStyles = (
  ...styles: Array<CSSProperties | undefined>
): CSSProperties | undefined => {
  styles = styles.filter((style) => !!style);
  if (!styles.length) {
    return;
  }
  let result: CSSProperties = {};
  for (const style of styles) {
    result = { ...result, ...style };
  }
  return result;
};
